import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { knowledgeApi } from '../../lib/knowledge-api';
import { useUIState } from '../../contexts/UIStateContext';
import { useTheme } from '../../hooks/useTheme';
import { useWebSocket } from '../../contexts/WebSocketContext';

export default function UploadDocumentModal({ isOpen, onClose, onDocumentImported }) {
  const { currentThemeType, themeTypes } = useTheme();
  const [file, setFile] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setKnowledgeImportProgress } = useUIState();
  const ws = useWebSocket();
  const preprocessingCallbackRef = useRef(null);

  const isLightTheme = currentThemeType === themeTypes.LIGHT;

  // Set up WebSocket listener for preprocessing completion
  useEffect(() => {
    const handlePreprocessingCompleted = (data) => {
      if (preprocessingCallbackRef.current) {
        preprocessingCallbackRef.current(data);
      }
    };

    const cleanupFn = ws.on('preprocessing_completed', handlePreprocessingCompleted);

    return () => {
      if (cleanupFn) cleanupFn();
    };
  }, [ws]);

  // Fetch products when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchProducts();
    }
  }, [isOpen]);

  const fetchProducts = async () => {
    try {
      const productList = await knowledgeApi.products.getAll();
      setProducts(productList);
      // Auto-select if only one product
      if (productList.length === 1) {
        setSelectedProductId(productList[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.name.endsWith('.pdf')) {
        toast.error('Please upload a PDF file');
        return;
      }

      // Validate file size (max 50MB)
      if (selectedFile.size > 50 * 1024 * 1024) {
        toast.error('File size must be less than 50MB');
        return;
      }

      setFile(selectedFile);
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    if (!selectedProductId) {
      toast.error('No product available. Please create a product first.');
      return;
    }

    // Close modal IMMEDIATELY
    const fileName = file.name;
    const fileToUpload = file;
    const productId = selectedProductId;

    handleClose();

    // Set initial progress state (no percentage during AI pre-processing)
    setKnowledgeImportProgress({
      fileName: fileName,
      status: 'processing',
      progress: 0,
      error: null,
      result: null,
    });

    // Set up WebSocket callback to handle preprocessing completion
    preprocessingCallbackRef.current = async (data) => {
      try {
        if (!data.success) {
          throw new Error(data.error || 'Processing failed');
        }

        // Step 2: Update progress - importing (no percentage during AI pre-processing)
        setKnowledgeImportProgress({
          fileName: fileName,
          status: 'importing',
          progress: 0,
          error: null,
          result: null,
        });

        // Execute import (creates placeholders, queues background metadata generation)
        await knowledgeApi.documentImport.executeImport(
          {
            collections: data.result.data.collections,
            items: data.result.data.items,
          },
          productId
        );

        // Step 3: Update progress - generating metadata (background job starts, percentages will come from WebSocket)
        setKnowledgeImportProgress({
          fileName: fileName,
          status: 'generating',
          progress: 0,
          error: null,
          result: null,
        });

        // Notify parent to refresh (will show placeholder titles)
        if (onDocumentImported) {
          onDocumentImported();
        }

        // Note: WebSocket will send final completion when metadata is done
      } catch (error) {
        console.error('Failed to import document:', error);

        // Set error state
        setKnowledgeImportProgress({
          fileName: fileName,
          status: 'error',
          progress: 0,
          error: error.message || 'Import failed',
          result: null,
        });
      } finally {
        // Clear the callback
        preprocessingCallbackRef.current = null;
      }
    };

    // Run import in background (async, don't await)
    (async () => {
      try {
        // Step 1: Start document processing (returns immediately, processing happens in background with heartbeats)
        const startResult = await knowledgeApi.documentImport.processDocument(fileToUpload, 50);

        if (!startResult.success) {
          throw new Error(startResult.message || 'Failed to start processing');
        }

        // Processing continues in background, WebSocket callback will handle completion
      } catch (error) {
        console.error('Failed to start document processing:', error);

        // Set error state
        setKnowledgeImportProgress({
          fileName: fileName,
          status: 'error',
          progress: 0,
          error: error.message || 'Failed to start import',
          result: null,
        });

        // Clear the callback since we failed
        preprocessingCallbackRef.current = null;
      }
    })();
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFile(null);
      setSelectedProductId(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={handleClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Upload Document</h3>
                    <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-4">
          {/* File Upload */}
          <div>
            <label className="text-gray-400 text-sm font-semibold mb-2 block">
              PDF Document <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center hover:border-purple-500 transition-colors">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
                disabled={isSubmitting}
              />
              {file ? (
                <div className={`flex items-center gap-4 p-4 rounded-lg border ${isLightTheme ? 'bg-gradient-to-br from-rose-50 to-pink-50 border-rose-200' : 'bg-gray-800 border-gray-700'}`} style={{ color: 'var(--color-text-on-accent)' }}>
                                  {/* PDF Icon Preview */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg flex items-center justify-center relative">
                      <div className={`absolute top-0 right-0 w-0 h-0 border-l-[16px] border-l-transparent border-t-[16px] rounded-tr-lg ${isLightTheme ? 'border-t-white/20' : 'border-t-gray-900/20'}`}></div>
                      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20" style={{ color: '#f0f0f0' }}>
                        <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                      </svg>
                      <div className="absolute bottom-1 left-0 right-0 text-center">
                        <span className="text-[10px] font-bold" style={{ color: '#f0f0f0' }}>PDF</span>
                      </div>
                    </div>
                  </div>

                  {/* File Info */}
                  <div className="flex-1 min-w-0 text-left">
                    <p className={`font-semibold text-sm truncate ${isLightTheme ? 'text-gray-800' : 'text-white'}`} title={file.name}>
                      {file.name}
                    </p>
                    <p className={`text-xs mt-1 ${isLightTheme ? 'text-gray-500' : 'text-gray-400'}`}>
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      <svg className={`w-3 h-3 ${isLightTheme ? 'text-green-600' : 'text-green-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className={`text-xs ${isLightTheme ? 'text-green-600' : 'text-green-400'}`}>Ready to import</span>
                    </div>
                  </div>

                  {/* Change File Button */}
                  <label htmlFor="file-upload" className="flex-shrink-0 cursor-pointer">
                    <div className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${isLightTheme ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'}`}>
                      Change
                    </div>
                  </label>
                </div>
              ) : (
                <label htmlFor="file-upload" className={`cursor-pointer block ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-white font-semibold">Click to upload PDF</p>
                  <p className="text-gray-400 text-sm mt-1">or drag and drop</p>
                  <p className="text-gray-500 text-xs mt-2">Maximum file size: 50MB</p>
                </label>
              )}
            </div>
          </div>

          {/* Product Selection (if multiple products exist) */}
          {products.length > 1 && (
            <div>
              <label className="text-gray-400 text-sm font-semibold mb-2 block">
                Product <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedProductId || ''}
                onChange={(e) => setSelectedProductId(e.target.value)}
                disabled={isSubmitting}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
              >
                <option value="">Select a product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} {product.version && `(${product.version})`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Info Message */}
          <div className={`rounded-lg p-4 ${isLightTheme ? 'bg-blue-50 border border-blue-200' : 'bg-blue-500/10 border border-blue-500/30'}`}>
            <div className="flex items-start gap-3">
              <svg className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isLightTheme ? 'text-blue-600' : 'text-blue-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className={`text-sm font-semibold mb-1 ${isLightTheme ? 'text-blue-800' : 'text-blue-300'}`}>Background Import</p>
                <p className={`text-xs ${isLightTheme ? 'text-blue-700' : 'text-blue-200'}`}>
                  The document will be imported in the background. You'll receive a notification when the import completes and metadata is generated.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-gray-700 flex justify-end items-center gap-3">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!file || !selectedProductId || isSubmitting}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            style={{ color: 'var(--color-text-on-accent)' }}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Starting Import...
              </>
            ) : (
              'Import Document'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
